<template>
  <v-text-field
    ref="input"
    v-model="lazyValue"
    :rules="validationRules"
    v-bind="$attrs"
    @blur="onBlur"
    @focus="onFocus"
    @input="onInput"
    @keydown.enter="onEnterKey"
  >
    <template #append>
      <slot name="append" />
    </template>
    <template #prepend>
      <slot name="prepend" />
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: 'TimeField',
  inheritAttrs: false,

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    displayIsoFormat: {
      type: Boolean,
      default: false,
    },

    returnMinutes: {
      type: Boolean,
      default: false,
    },

    rules: {
      type: Array,
      default: _ => [],
    },

    value: {
      type: [String, Number],
      default: '',
    },
  },

  data () {
    return {
      hourDayStartsAt: 7,
      isFocused: false,
      lazyValue: '',
    }
  },

  computed: {
    validationRules () {
      return [
        ...this.rules,
        this.validateTimeValue,
      ]
    },
  },

  watch: {
    value (value) {
      if (this.isFocused) return
      this.onValueChanged(value)
    },
  },

  created () {
    this.onValueChanged(this.value)
  },

  methods: {
    getParsedValue (value) {
      const parsed = this.parseTime(value)
      if (typeof parsed === 'string') return ''

      const [hours, minutes, meridiem] = parsed
      return this.displayIsoFormat ? `${hours}:${minutes}` : `${hours}:${minutes} ${meridiem}`
    },

    onBlur () {
      this.lazyValue = this.getParsedValue(this.lazyValue)
      this.isFocused = false
    },

    onEnterKey () {
      this.$emit('enter-key')
    },

    onFocus () {
      this.isFocused = true
    },

    onInput () {
      if (this.returnMinutes) {
        const parsed = this.parseTime(this.lazyValue)
        if (typeof parsed === 'string') return ''

        const hour = parseInt(parsed[0])
        const minute = parseInt(parsed[1])
        const meridiem = parsed[2]

        const value = (hour * 60) + minute + (meridiem === 'PM' ? 720 : 0)

        this.$emit('input', value)
      } else {
        this.$emit('input', this.getParsedValue(this.lazyValue))
      }
    },

    onValueChanged (value) {
      if (!value) {
        this.lazyValue = ''
        return
      }

      if (this.returnMinutes) {
        const hour = Math.floor(value / 60)
        const minute = value - (hour * 60)
        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

        this.lazyValue = this.validateTimeValue(time) === true ? this.getParsedValue(time) : ''
      } else {
        this.lazyValue = this.validateTimeValue(value) === true ? this.getParsedValue(value) : ''
      }
    },

    parseIsoTime (value) {
      const timeString = (value || '').trim().toString()
      const timeWithColonRegex = /^([0-9]{1,2}):([0-9]{1,2})$/
      const timeWithoutColonRegex = /^([0-9]{1,4})(\s*)$/

      const match = timeString.includes(':')
        ? timeString.match(timeWithColonRegex)
        : timeString.match(timeWithoutColonRegex)

      if (!match) return 'Expected format HH:MM'

      let hour = match[1] || ''
      let minute = parseInt(match[2]) || '0'

      if (hour.length > 2) {
        minute = parseInt(hour.substring(hour.length - 2))
        hour = parseInt(hour.substring(0, hour.length - 2))
      } else {
        hour = parseInt(hour)
      }

      if (hour > 23 || minute > 59) {
        return 'Invalid time value'
      }

      return [
        String(hour).padStart(2, '0'),
        String(minute).padStart(2, '0'),
      ]
    },

    parseTime (value) {
      return this.displayIsoFormat ? this.parseIsoTime(value) : this.parseUSTime(value)
    },

    parseUSTime (value) {
      const timeString = (value || '').trim().toString()
      const timeWithColonRegex = /^([0-9]{1,2}):([0-9]{1,2})\s*([AaPp][Mm]?)?$/
      const timeWithoutColonRegex = /^([0-9]{1,4})(\s*)([AaPp][Mm]?)?$/

      const match = timeString.includes(':')
        ? timeString.match(timeWithColonRegex)
        : timeString.match(timeWithoutColonRegex)

      if (!match) return 'Expected format HH:MM AM/PM'

      let hour = match[1] || ''
      let minute = parseInt(match[2]) || '0'

      if (hour.length > 2) {
        minute = parseInt(hour.substring(hour.length - 2))
        hour = parseInt(hour.substring(0, hour.length - 2))
      } else {
        hour = parseInt(hour)
      }

      let meridiem = (match[3] || '').trim().toUpperCase()

      if (meridiem === 'A' || meridiem === 'P') meridiem += 'M'

      if (!meridiem) {
        if (hour === 0) {
          hour = 12
          meridiem = 'AM'
        } else if (hour > 12 && hour < 24) {
          hour -= 12
          meridiem = 'PM'
        } else if (hour < this.hourDayStartsAt || hour === 12) {
          meridiem = 'PM'
        } else {
          meridiem = 'AM'
        }
      }

      if (hour < 1 || hour > 12 || minute > 59) {
        return 'Invalid time value'
      }

      return [
        String(hour).padStart(2, '0'),
        String(minute).padStart(2, '0'),
        meridiem,
      ]
    },

    validateTimeValue (value) {
      const parsed = this.parseTime(value)
      if (typeof parsed === 'string' && !!parsed) return parsed
      return true
    },
  },
}
</script>
